import * as _ from 'lodash';

import { Injectable } from '@angular/core';
import { _ti } from '@wephone-translation';
import { DidEntity } from '@wephone-core/model/entity/did';
import { Deferred } from 'ts-deferred';
import { DialogService, LoggerService } from '@wephone-utils';
import { SipTrunkEntity } from '@wephone-core/model/entity/siptrunk';
import { SipTrunkConnectionLogEntity } from '@wephone-core/model/entity/siptrunk_connection_log';
import { SipTrunkConnectionLogRepository } from '@wephone-core/model/repository/siptrunk_connection_log';
import { EntityManager } from '@wephone-core/wephone-core.module';
import { HttpEngine } from '@wephone-core/service/http_engine';

@Injectable()
export class SipTrunkService {

  static GetSipTrunkConnectionLog = `sip-trunk/connection_log/{trunk_id}?limit={limit}`;

  // Constructor
  constructor(
    private readonly em: EntityManager,
    private readonly httpEngine: HttpEngine,
    private readonly dialogService: DialogService,
    private readonly loggerService: LoggerService
  ) { }

  async getConfirmDeleteSipTrunks(sipTrunks: SipTrunkEntity[]): Promise<boolean> {
    let msg: string = _ti('user.title.delete');
    const routedDids: DidEntity[] = [];
    const msgs: string[] = [];

    for (const sipTrunk of sipTrunks) {
      for (const routedDid of (await sipTrunk.routedDids)) {
        if (!_.includes(routedDids, routedDid)) {
          routedDids.push(routedDid);
        }
      }
    }

    if (routedDids.length) {
      let sipTrunkObjects: SipTrunkEntity[] = [];
      for (const i of sipTrunks) {
        const _routedDids = await i.routedDids;
        if (_routedDids.length) {
          sipTrunkObjects.push(i);
        }
      }

      sipTrunkObjects = _.uniqBy(sipTrunkObjects, i => i.id);

      msgs.push(_ti('did.message.confirm_delete_with_dependent_did', {
        dependent_numbers: routedDids.map(x => x.display_number).join(', '),
        objects: sipTrunkObjects.map(x => x.name).join(', '),
      }));
    }

    const ret = new Deferred<boolean>();
    const canDelete = !msgs.length;

    if (!canDelete) {
      msgs.push(_ti('public.message.cannot_continue_action_with_dependent_items'));
      msg = msgs.join('\n');

      await this.dialogService.showAlert(_ti('dialogs.warning'), msg);

      ret.resolve(canDelete);
      return ret.promise;
    }

    await this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      msg,
      () => {
        ret.resolve(canDelete);
      }
    );
    return ret.promise;
  }

  transformData(formData: any): any {
    const data: any = {};
    const attrList = Object.keys(formData);
    for (const attr of attrList) {
      switch (attr) {
        case 'outgoing_enabled':
        case 'sip_ping_enable':
        case 'is_default':
        case 'backup_number_enable':
          data[attr] = formData[attr] ? 1 : 0;
          break;
        default:
          // If backup_number_enable is false, skip update backup_number
          if (attr === 'backup_number' && attrList.find(f => f === 'backup_number_enable') && !formData.backup_number_enable) {
            break;
          }

          data[attr] = formData[attr];
          break;
      }
    }
    return data;
  }

  async getSipTrunkConnectionLog(sipTrunkId: number, limit: number = 10): Promise<SipTrunkConnectionLogEntity[]> {
    try {
      const repo: SipTrunkConnectionLogRepository = this.em.getRepository('SipTrunkConnectionLogRepository');
      const url: string = SipTrunkService.GetSipTrunkConnectionLog
        .replace('{trunk_id}', sipTrunkId.toString())
        .replace('{limit}', limit.toString());
      const ret: SipTrunkConnectionLogEntity[] = [];

      const list = await this.httpEngine.apiGetV2(url);
      for (const l of list) {
        ret.push(repo.create(l) as SipTrunkConnectionLogEntity);
      }

      return ret;
    } catch (error) {
      this.loggerService.error('getSipTrunkConnectionLog failure', error);
      return [];
    }
  }
}
